<template>
  <iColumn
    class="chatroom-thread-container"
    gap="none"
    height="fill"
    wrap="nowrap"
  >
    <ChatroomPanelHeader
      v-if="hasChatRoomHeader"
      :context="context"
      :entity-id="entityId"
      :conversation-id="shownConversationId"
      :show-conversation-actions="showHeaderActions"
      :show-border="true"
      @open-details-modal="openChannelDetailsModal"
      @open-members-modal="openChannelMembersModal"
      @open-settings-modal="openChannelSettingsModal"
      @open-video-call="navigateToVideoCall"
    />
    <iRow v-if="isMounted" height="fill" overflow="scroll">
      <ConversationThread
        ref="conversationThread"
        :entity-id="entityId"
        :context="context"
        :selected-conversation-id="conversationId"
      />
    </iRow>
    <ChatroomUserActions
      v-if="hasChatRoomActions"
      ref="chatroomUserActions"
      :channel-href="channelHref"
      :channel-name="channelName"
      :context="context"
      :conversation-id="conversationId"
      :description="description"
      :entity-id="entityId"
      @message-sent="scrollToBottom"
    />
    <VideoCallModal
      v-if="shouldShowVideoModal"
      :conversation-id="videoCallConversationId"
      :show-modal="isVideoCallModalOpen"
      @close="handleVideoModalClose"
    />
  </iColumn>
</template>

<script>
import { useChannelStore } from "~/stores/channel-store";
import { mapActions, mapState } from "pinia";
import ChatroomUserActions from "%/components/community/chatrooms/slack-style/ChatroomUserActions.vue";
import { useGeneralStore } from "%/stores/general-store";
import ConversationThread from "%/apps/conversations/components/ConversationThread.vue";
import ChatroomPanelHeader from "%/apps/conversations/components/ChatroomPanelHeader.vue";
import { useConversationStore } from "%/apps/conversations/stores/conversation-store";
import VideoCallModal from "%/apps/conversations/components/modals/VideoCallModal.vue";
import { useVideoCallStore } from "%/apps/video-engine/stores/video-call-store";

export default {
  name: "Chatroom",
  components: {
    ConversationThread,
    ChatroomPanelHeader,
    ChatroomUserActions,
    VideoCallModal,
  },
  props: {
    context: {
      type: String,
      required: true,
    },
    entityId: {
      type: Number,
      required: true,
    },
    conversationId: {
      type: Number,
      required: false,
      default: 0,
    },
    showHeaderActions: {
      type: Boolean,
      required: false,
      default: true,
    },
    hasChatRoomActions: {
      type: Boolean,
      required: false,
      default: true,
    },
    hasChatRoomHeader: {
      type: Boolean,
      required: false,
      default: true,
    },
    showDeletedMessages: {
      type: Boolean,
      required: false,
      default: false,
    },
    unauthorizedViewing: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      isMounted: false,
    };
  },
  computed: {
    ...mapState(useGeneralStore, ["isLoggedIn", "userData"]),
    ...mapState(useChannelStore, ["currentChannel", "directMessages", "activeConversationId", "activeEntityId"]),
    ...mapState(useConversationStore, ["conversationData"]),
    ...mapState(useVideoCallStore, ["videoCalls"]),
    isVideoCallActive() {
      const activeParticipants = this.conversationData[this.activeConversationId]?.active_participants_count || 0;
      return activeParticipants > 0;
    },
    videoCallConversationId() {
      return this.activeConversationId || this.shownConversationId;
    },
    isVideoCallModalOpen() {
      return this.videoCalls[this.videoCallConversationId] || false;
    },
    shouldShowVideoModal() {
      return this.videoCallConversationId && (this.isVideoCallModalOpen || this.isVideoCallActive);
    },
    channelName() {
      return this.currentChannel?.channel_name || "";
    },
    description() {
      return this.currentChannel?.description || "";
    },
    channelHref() {
      return this.currentChannel?.href || "";
    },
    shownConversationId() {
      return this.conversationId || this.currentChannel?.conversation_id || 0;
    },
  },
  mounted() {
    this.isMounted = true;
  },
  methods: {
    scrollToBottom() {
      if (this.$refs.conversationThread?.scrollToBottom) {
        this.$refs.conversationThread.scrollToBottom();
      }
    },
    ...mapActions(useGeneralStore, ["getRelativeHref"]),
    ...mapActions(useChannelStore, ["fetchChannelCreatorDetails"]),
    ...mapActions(useVideoCallStore, ["openVideoCall", "closeVideoCall"]),
    openChannelMembersModal() {
      if (this.$refs.chatroomUserActions) {
        this.$refs.chatroomUserActions.openShowMembersModal();
      }
    },
    openChannelDetailsModal() {
      if (this.$refs.chatroomUserActions) {
        this.$refs.chatroomUserActions.openShowDetailsModal();
      }
    },
    navigateToVideoCall() {
      this.openVideoCall(this.videoCallConversationId);
    },
    handleVideoModalClose() {
      this.closeVideoCall(this.videoCallConversationId);
    },
    openChannelSettingsModal() {
      if (this.$refs.chatroomUserActions) {
        this.$refs.chatroomUserActions.openShowSettingsModal();
      }
    },
  },
  styleGuide: () => ({
    borderTopWidth: { "size.border": "thin" },
    borderTopColor: { "color.border": "standard" },
  }),
};
</script>

<style lang="scss" scoped>
.chatroom {
  width: 100%;
}
</style>
